import { ArrowDownward, ArrowUpward, Science } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ConnectLabsDialog from 'components/manage-labs/connect-labs-dialog/connect-labs-dialog.component';
import ManageLabsActions from 'components/manage-labs/manage-labs-actions/manage-labs-actions.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { useLabs } from '../../context/labs-context';

const ManageLabsComponent: FC = () => {
    const { t: translate } = useTranslation(['common', 'organization', 'orders', 'lab-settings']);
    const navigate = useNavigate();
    const [connectDialogOpen, setConnectDialogOpen] = useState(false);
    const {
        connectedLabs,
        isLoading,
        error,
        sortOrder,
        setSortOrder,
        isSorting,
        setSearchTerm,
        searchTerm,
        isSearching,
        hasMore,
        loadMore,
        statusFilter,
        setStatusFilter,
    } = useLabs();

    const skeletonColumns: SkeletonColumnConfig[] = [
        { type: 'avatar-text', width: 120 },
        { type: 'text', width: 80 },
        { type: 'text', width: 100 },
        { type: 'button', width: 80 },
        { type: 'button', width: 80 },
    ];

    const handleSort = async () => {
        await setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const handleSearch = (searchTerm: string) => {
        setSearchTerm(searchTerm);
    };

    const handleStatusFilter = (status: 'all' | 'active' | 'inactive') => {
        setStatusFilter(status);
    };

    const handleConnectLabs = () => {
        setConnectDialogOpen(true);
    };

    const handleCloseConnectDialog = () => {
        setConnectDialogOpen(false);
    };

    const handleShowLab = (labId: string) => {
        navigate(`/action-based/manage-labs/availability/${labId}`);
    };

    const handleEditLab = (labId: string) => {
        navigate(`/action-based/manage-labs/edit-lab/${labId}`);
    };

    if (error) return <Typography color="error">Error: {error}</Typography>;

    return (
        <Box>
            <PageTitle headingStyle="h1" title={translate('organization:MANAGE_LABS')} />
            <ManageLabsActions
                onSearch={handleSearch}
                onConnectLabs={handleConnectLabs}
                onStatusFilter={handleStatusFilter}
                statusFilter={statusFilter}
                searchTerm={searchTerm}
            />
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={connectedLabs.length}
                    next={loadMore}
                    hasMore={hasMore}
                    loader={undefined}
                    throttleTimeInMs={100}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {translate('common:LAB')}
                                    <IconButton size="small" onClick={handleSort}>
                                        {sortOrder === 'asc' ? (
                                            <ArrowUpward fontSize="small" />
                                        ) : (
                                            <ArrowDownward fontSize="small" />
                                        )}
                                    </IconButton>
                                </TableCell>
                                <TableCell>{translate('orders:RATING')}</TableCell>
                                <TableCell>{translate('lab-settings:PRICE_LISTS')}</TableCell>
                                <TableCell>{translate('organization:AVAILABILITY')}</TableCell>
                                <TableCell>{translate('organization:EDIT_LAB')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(isLoading && connectedLabs.length === 0) || isSorting || isSearching ? (
                                <SkeletonTable columns={skeletonColumns} rows={3} renderRowsOnly={true} />
                            ) : connectedLabs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 8 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 2,
                                            }}
                                        >
                                            <Science sx={{ fontSize: 64, color: 'text.secondary' }} />
                                            <Typography variant="h6" color="text.secondary">
                                                {translate('organization:NO_CONNECTED_LABS_FOUND')}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                connectedLabs.map((lab) => (
                                    <TableRow key={lab.lab._id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {lab.lab.logoSignedUrl ? (
                                                    <img
                                                        src={lab.lab.logoSignedUrl}
                                                        alt={lab.lab.name}
                                                        style={{
                                                            width: 52,
                                                            height: 52,
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : (
                                                    <Avatar sx={{ width: 52, height: 52, bgcolor: 'grey.500' }}>
                                                        {lab.lab.name?.charAt(0)?.toUpperCase()}
                                                    </Avatar>
                                                )}
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Typography>{lab.lab.name}</Typography>
                                                    <Typography>{lab.lab.organizationNumber}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {lab.ratePercent?.toFixed(2)}% ({lab.totalRating})
                                        </TableCell>
                                        <TableCell>
                                            {lab.categoryNames?.length && !lab.isDisabled ? (
                                                lab.categoryNames?.map((cat, index) => (
                                                    <Typography key={index} variant="body2" sx={{ display: 'block' }}>
                                                        • {cat}
                                                    </Typography>
                                                ))
                                            ) : (
                                                <>
                                                    {lab.isDisabled ? (
                                                        <Typography variant="body2" color="error">
                                                            {translate('organization:LAB_DISABLE')}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            -
                                                        </Typography>
                                                    )}
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleShowLab(lab.lab._id)}
                                            >
                                                {translate('common:SHOW')}
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleEditLab(lab.lab._id)}
                                            >
                                                {translate('organization:EDIT_LAB')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
            <ConnectLabsDialog open={connectDialogOpen} onClose={handleCloseConnectDialog} />
        </Box>
    );
};

export default ManageLabsComponent;
